/**
 * ContentSectionHelper
 *
 * @author Copyright (c) 2021 Salesforce.com.
 * @ownership Appexchange Everywhere
 */

import { i18nStore } from '~/modules/stores/i18nStore';
import { Section, SectionTypes } from '../../schemas/content-schema-v1';
import { parseCustomerStory } from './contentFeedHelper';

export interface ContentShowMore {
    label: string;
    url: string;
    paths: Record<string, string>;
}

// @ts-expect-error * Automated Type Error Fix
const buildSectionUrl = (section): string => {
    switch (section.type) {
        case SectionTypes.RECOMMENDATIONS:
            return section.tag.pop().type;
        case SectionTypes.TOP_TRENDING:
            // If relative url doesnt use the new URL unless we fixed to a Domain
            return (section.url as string).startsWith('/') ? section.url : new URL(section.url).toString();
        default:
            // @ts-expect-error * Automated Type Error Fix
            return undefined;
    }
};

// @ts-expect-error * Automated Type Error Fix
export function buildSection(section): Section {
    const url = buildSectionUrl(section);
    let props = {
        type: section.type,
        id: section.id,
        url,
    };
    const result = {
        type: section.type,
        props,
    };
    switch (section.type) {
        case SectionTypes.WORDPRESS_SLUG_CONTENT: {
            props = Object.assign(props, {
                positions: section.positions,
                spreadProps: {
                    slug: section?.props?.slug,
                    section: section?.props?.section,
                },
            });
            break;
        }
        case SectionTypes.TOAST: {
            props = Object.assign(props, { toasts: section.wrapper.toasts });
            break;
        }

        default: {
            props = Object.assign(props, { title: section.title });
            const viewMore = section.layout?.viewMoreUrl;
            const viewMorePaths = section.layout?.viewMorePaths;
            const variant = section.layout?.variant;
            const unauthenticated_dimensions = section.layout?.dims?.unauthenticated;
            const authenticated_dimensions = section.layout?.dims?.authenticated;
            const tooltip = section.tooltip;
            const positions = section.positions;

            if (viewMore) {
                let paths = null;
                if (viewMorePaths) {
                    paths = viewMorePaths;
                }

                props = Object.assign(props, {
                    url: section.url || url,
                    _showMore: {
                        label: section.layout.label || i18nStore.getState().i18nStaticCache.homePage.showMore,
                        url: viewMore,
                        paths: paths,
                    } as ContentShowMore,
                });
            }

            if (unauthenticated_dimensions) {
                props = Object.assign(props, {
                    unauthenticated_dimensions: unauthenticated_dimensions,
                });
            }

            if (authenticated_dimensions) {
                props = Object.assign(props, {
                    authenticated_dimensions: authenticated_dimensions,
                });
            }

            if (variant) {
                props = Object.assign(props, {
                    variant,
                });
            }

            if (tooltip) {
                props = Object.assign(props, { tooltip });
            }

            // TODO: Wrappers is a confusing concept that is not documented anywhere.
            // If you need to touch this code, consider refactoring them out of existence.
            if (section.wrappers) {
                props = Object.assign(props, {
                    // @ts-expect-error * Automated Type Error Fix
                    records: section.wrappers.map((customerStory) => parseCustomerStory(customerStory)),
                });
            }

            if (positions) {
                props = Object.assign(props, { positions });
            }
        }
    }
    return result;
}

export function buildHomeContentSections(data: AnyObject[]): Section[] {
    return data
        .filter((section) => section && Object.values(SectionTypes).includes(section.type as SectionTypes))
        .map((section) => buildSection(section));
}
